import { useEffect, useRef, useState } from "react";
import { ACCENT } from "./landingTheme";

// Vitrine de recursos em vídeo/GIF — usada pela landing (emissoras) e pela
// página de podcast.
//
// Os arquivos moram em `public/landing/`, NUNCA importados por `import`: são
// 23 MB e um import os colocaria dentro de um chunk JavaScript, que foi
// exatamente o que derrubou a apresentação do Mural na v963. Em public/ o
// react-scripts os copia byte a byte, sem passar por webpack.

/*
 * Modal com o vídeo completo (com locução). Toca ao abrir; fecha pelo X, por
 * clique fora ou Esc — e o vídeo para junto, porque o <video> só existe
 * enquanto o modal está aberto. A rolagem da página fica travada no meio.
 */
function ModalVideo({ titulo, src, poster, onFechar }) {
  const fecharRef = useRef(null);

  useEffect(() => {
    const aoTeclar = (e) => { if (e.key === "Escape") onFechar(); };
    const overflowAntes = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", aoTeclar);
    fecharRef.current?.focus();
    return () => {
      document.body.style.overflow = overflowAntes;
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [onFechar]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      onClick={onFechar}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "clamp(1rem, 4vw, 3rem)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ position: "relative", width: "min(1100px, 100%)" }}
      >
        <button
          ref={fecharRef}
          type="button"
          onClick={onFechar}
          aria-label="Fechar vídeo"
          style={{
            position: "absolute",
            top: -44,
            right: 0,
            width: 36,
            height: 36,
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.25)",
            background: "rgba(255,255,255,0.08)",
            color: "#fff",
            fontSize: 20,
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          ×
        </button>
        <video
          src={src}
          poster={poster}
          controls
          autoPlay
          playsInline
          style={{
            width: "100%", display: "block", borderRadius: 12, background: "#000", boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
          }}
        />
      </div>
    </div>
  );
}

/*
 * Destaque: GIF rodando sozinho (o olho entende em segundos) com um play por
 * cima; o clique abre o vídeo completo, com locução, num modal.
 *
 * O GIF tem `loading="lazy"` e o vídeo só é baixado quando o modal abre —
 * abrir a landing não custa os ~5 MB de cada vídeo.
 */
export function VideoDestaque({
  titulo, descricao, src, gif, poster, invertido = false,
}) {
  const [aberto, setAberto] = useState(false);
  const [foco, setFoco] = useState(false);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.25fr)",
        gap: "clamp(1.5rem, 4vw, 4rem)",
        alignItems: "center",
        // No celular a ordem visual tem que ser texto → vídeo sempre; sem
        // isto, a linha invertida deixaria o vídeo antes do título que o
        // explica.
        gridAutoFlow: "row",
      }}
      className={`vitrine-linha${invertido ? " vitrine-linha--invertida" : ""}`}
    >
      <div style={{ order: invertido ? 2 : 1 }}>
        <h3
          style={{
            fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.15,
            margin: "0 0 0.9rem",
            color: "#fff",
          }}
        >
          {titulo}
        </h3>
        <p
          style={{
            fontSize: "clamp(0.95rem, 1.2vw, 1.05rem)",
            color: "rgba(255,255,255,0.45)",
            lineHeight: 1.65,
            margin: 0,
          }}
        >
          {descricao}
        </p>
      </div>

      <button
        type="button"
        onClick={() => setAberto(true)}
        onMouseEnter={() => setFoco(true)}
        onMouseLeave={() => setFoco(false)}
        onFocus={() => setFoco(true)}
        onBlur={() => setFoco(false)}
        aria-label={`Assistir ao vídeo: ${titulo}`}
        style={{
          order: invertido ? 1 : 2,
          position: "relative",
          padding: 0,
          cursor: "pointer",
          borderRadius: 14,
          overflow: "hidden",
          border: `1px solid ${foco ? ACCENT : "rgba(255,255,255,0.10)"}`,
          boxShadow: "0 24px 70px rgba(0,0,0,0.65)",
          background: "#111",
          lineHeight: 0,
          transition: "border-color 0.2s ease",
        }}
      >
        <img src={gif} alt={titulo} loading="lazy" style={{ width: "100%", display: "block" }} />
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: foco ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0)",
            transition: "background 0.2s ease",
          }}
        >
          <span
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: ACCENT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
              transform: foco ? "scale(1.08)" : "scale(1)",
              transition: "transform 0.2s ease",
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z" /></svg>
          </span>
        </span>
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 14,
            bottom: 14,
            padding: "6px 12px",
            borderRadius: 999,
            background: "rgba(0,0,0,0.65)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          ▶ Assistir com áudio
        </span>
      </button>
      {aberto && <ModalVideo titulo={titulo} src={src} poster={poster} onFechar={() => setAberto(false)} />}
    </div>
  );
}

/*
 * Cartão de recurso com GIF. GIF roda sozinho, sem controle e sem som — é o
 * formato certo para o olho passar e entender em dois segundos, enquanto o
 * vídeo com locução é para quem parou e quer ouvir.
 *
 * `loading="lazy"` porque são seis: sem isso a landing baixaria ~6 MB de GIF
 * antes de o visitante chegar perto da seção.
 */
export function CardGif({ titulo, descricao, src }) {
  return (
    <div
      style={{
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.09)",
        background: "#0d0d0d",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <img
        src={src}
        alt={titulo}
        loading="lazy"
        style={{ width: "100%", display: "block", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      />
      <div style={{ padding: "1.1rem 1.25rem 1.3rem" }}>
        <div style={{ fontSize: "1.02rem", fontWeight: 600, color: "#fff", marginBottom: "0.4rem" }}>
          {titulo}
        </div>
        <div style={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.42)", lineHeight: 1.6 }}>
          {descricao}
        </div>
      </div>
    </div>
  );
}

/*
 * No celular a grade de duas colunas vira uma, e a ordem passa a ser SEMPRE
 * texto → vídeo. Sem isto, a linha invertida colocaria o vídeo acima do
 * título que o explica — o visitante veria a tela antes de saber o que está
 * olhando. Vai como <style> porque `order` depende de media query, que estilo
 * inline não alcança.
 */
export function VitrineEstilos() {
  return (
    <style>{`
      @media (max-width: 860px) {
        .vitrine-linha { grid-template-columns: 1fr !important; }
        .vitrine-linha > *:first-child { order: 1 !important; }
        .vitrine-linha > *:last-child  { order: 2 !important; }
      }
    `}</style>
  );
}

export function TituloSecao({ etiqueta, titulo, subtitulo }) {
  return (
    <div style={{ marginBottom: "clamp(2rem, 5vw, 3.5rem)", maxWidth: 760 }}>
      {etiqueta && (
        <p
          style={{
            fontSize: "0.8rem",
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: ACCENT,
            margin: "0 0 0.9rem",
          }}
        >
          {etiqueta}
        </p>
      )}
      <h2
        style={{
          fontSize: "clamp(2rem, 4vw, 3.2rem)",
          fontWeight: 700,
          letterSpacing: "-0.03em",
          lineHeight: 1.1,
          margin: "0 0 1rem",
          color: "#fff",
        }}
      >
        {titulo}
      </h2>
      {subtitulo && (
        <p
          style={{
            fontSize: "clamp(1rem, 1.3vw, 1.15rem)",
            color: "rgba(255,255,255,0.45)",
            lineHeight: 1.65,
            margin: 0,
          }}
        >
          {subtitulo}
        </p>
      )}
    </div>
  );
}
